import { useEffect, useState } from "react";
import { Menu, X, FileDown } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToSection } from "../../lib/scroll";
import { useLang } from "../../i18n/LanguageContext";

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex overflow-hidden rounded-full border border-white/15" data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          aria-pressed={lang === l}
          className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
            lang === l ? "bg-vg-green text-vg-black" : "text-vg-muted hover:text-vg-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-white/10 bg-vg-black/80 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.45)]" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("hero")} data-testid="nav-logo-button" aria-label="EVGP Italia 2027 — torna su" className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-vg-muted transition-colors duration-200 hover:text-vg-green"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <LangToggle />
          </div>
          <button
            onClick={() => go("scuole")}
            data-testid="nav-cta-adesioni"
            className="hidden items-center gap-2 rounded-full bg-vg-green px-5 py-2.5 font-display text-sm font-semibold uppercase tracking-wider text-vg-black transition-all duration-200 hover:shadow-[0_0_28px_rgba(16,185,129,0.5)] md:inline-flex"
          >
            <FileDown className="h-4 w-4" />
            {t.nav.cta}
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Apri menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-white/10 bg-vg-card text-vg-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-vg-black lg:hidden" data-testid="nav-mobile-menu">
          <div className="space-y-1 px-5 py-4">
            {t.nav.links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="block w-full rounded-md px-3 py-3 text-left font-display text-lg font-semibold uppercase tracking-wider text-vg-white transition-colors hover:bg-vg-asphalt hover:text-vg-green"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center justify-between gap-3 pt-2">
              <button
                onClick={() => go("scuole")}
                data-testid="nav-mobile-cta"
                className="flex-1 rounded-full bg-vg-green px-5 py-3 text-center font-display text-base font-semibold uppercase tracking-wider text-vg-black"
              >
                {t.nav.cta}
              </button>
              <div className="sm:hidden">
                <LangToggle />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
